import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMotoristaApi, getMotoristaUser, getMotoristaToken } from '../../services/motoristaAuth';
import { Config } from '../../services/config';

const MotoristaChecklist: React.FC = () => {
  const navigate = useNavigate();
  const motorista = getMotoristaUser();
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [veiculoId, setVeiculoId] = useState('');
  const [odometro, setOdometro] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<{ descricao: string; conforme: boolean | null; obs: string }[]>([
    { descricao: 'Nivel do oleo do motor', conforme: null, obs: '' },
    { descricao: 'Nivel de agua do radiador', conforme: null, obs: '' },
    { descricao: 'Estado dos pneus (sulco e pressao)', conforme: null, obs: '' },
    { descricao: 'Funcionamento dos farois e lanternas', conforme: null, obs: '' },
    { descricao: 'Funcionamento das setas e pisca-alerta', conforme: null, obs: '' },
    { descricao: 'Funcionamento dos freios', conforme: null, obs: '' },
    { descricao: 'Estado dos retrovisores', conforme: null, obs: '' },
    { descricao: 'Cinto de seguranca', conforme: null, obs: '' },
    { descricao: 'Extintor de incendio (validade e pressao)', conforme: null, obs: '' },
    { descricao: 'Triangulo de sinalizacao', conforme: null, obs: '' },
    { descricao: 'Documentacao do veiculo (CRLV)', conforme: null, obs: '' },
    { descricao: 'Limpeza geral do veiculo', conforme: null, obs: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchMotoristaApi('/veiculos').then(res => {
      if (res.success) setVeiculos(res.data || []);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, []);

  const setItemConforme = (index: number, value: boolean) => {
    setItens(prev => prev.map((item, i) => i === index ? { ...item, conforme: value } : item));
  };

  const setItemObs = (index: number, obs: string) => {
    setItens(prev => prev.map((item, i) => i === index ? { ...item, obs } : item));
  };

  const todosRespondidos = itens.every(i => i.conforme !== null);
  const temNaoConformidade = itens.some(i => i.conforme === false);

  const handleSubmit = async () => {
    if (!veiculoId) { alert('Selecione um veiculo'); return; }
    if (!todosRespondidos) { alert('Responda todos os itens do checklist'); return; }

    setIsSubmitting(true);
    try {
      // Usar a API de checklists existente
      const token = getMotoristaToken();
      const response = await fetch(`${Config.API_BASE_URL}/checklists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          veiculo_id: parseInt(veiculoId),
          motorista_id: motorista?.id,
          tipo: 'pre_viagem',
          status: 'concluido',
          origem: 'app',
          odometro: odometro ? parseInt(odometro) : undefined,
          observacoes,
          itens: itens.map((item, index) => ({
            descricao: item.descricao,
            categoria: 'inspecao_diaria',
            tipo: 'booleano',
            valor: item.conforme ? 'sim' : 'nao',
            observacao: item.obs || undefined,
            ordem: index + 1,
            obrigatorio: true
          }))
        })
      });

      const result = await response.json();

      if (result.success || result.sucesso) {
        alert('Checklist enviado com sucesso!');
        navigate('/motorista');
      } else {
        alert(result.message || result.mensagem || 'Erro ao enviar checklist');
      }
    } catch (err) {
      alert('Erro de conexao. Tente novamente.');
    } finally { setIsSubmitting(false); }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f6fa' }}>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6fa' }}>
      {/* Header */}
      <div style={{ background: '#04C64F', color: 'white', padding: '15px 20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button onClick={() => navigate('/motorista')} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1 style={{ margin: 0, fontSize: '1.1rem' }}>Checklist Pre-Viagem</h1>
      </div>

      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        {/* Selecao de veiculo */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>Veiculo *</label>
          <select value={veiculoId} onChange={(e) => setVeiculoId(e.target.value)}
            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem' }}>
            <option value="">Selecione o veiculo</option>
            {veiculos.map((v: any) => <option key={v.id} value={v.id}>{v.placa} - {v.marca} {v.modelo}</option>)}
          </select>

          <label style={{ display: 'block', marginTop: '15px', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>Odometro (km)</label>
          <input type="number" value={odometro} onChange={(e) => setOdometro(e.target.value)}
            placeholder="Km atual" inputMode="numeric"
            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem', boxSizing: 'border-box' }} />
        </div>

        {/* Itens do checklist */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '1rem', color: '#2c3e50', marginBottom: '15px' }}>Itens de Inspecao</h3>

          {itens.map((item, index) => (
            <div key={index} style={{ padding: '12px 0', borderBottom: index < itens.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#2c3e50', fontWeight: '500' }}>
                {index + 1}. {item.descricao}
              </p>
              <div style={{ display: 'flex', gap: '10px', marginBottom: item.conforme === false ? '8px' : '0' }}>
                <button
                  onClick={() => setItemConforme(index, true)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
                    background: item.conforme === true ? '#04C64F' : '#e8f5e9',
                    color: item.conforme === true ? 'white' : '#04C64F'
                  }}
                >
                  <i className="fas fa-check" style={{ marginRight: '6px' }}></i>OK
                </button>
                <button
                  onClick={() => setItemConforme(index, false)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
                    background: item.conforme === false ? '#e74c3c' : '#fce4ec',
                    color: item.conforme === false ? 'white' : '#e74c3c'
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '6px' }}></i>Problema
                </button>
              </div>
              {item.conforme === false && (
                <input
                  type="text" value={item.obs} onChange={(e) => setItemObs(index, e.target.value)}
                  placeholder="Descreva o problema..."
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e74c3c', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Observacoes gerais */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>Observacoes Gerais</label>
          <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Observacoes adicionais..." rows={3}
            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        {/* Resumo */}
        {todosRespondidos && (
          <div style={{ background: temNaoConformidade ? '#fff3e0' : '#e8f5e9', borderRadius: '12px', padding: '15px', marginBottom: '15px', textAlign: 'center' }}>
            <i className={`fas ${temNaoConformidade ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}
              style={{ fontSize: '24px', color: temNaoConformidade ? '#f57c00' : '#04C64F', marginBottom: '8px', display: 'block' }}></i>
            <strong>{temNaoConformidade ? `${itens.filter(i => !i.conforme).length} problema(s) encontrado(s)` : 'Todos os itens conformes!'}</strong>
          </div>
        )}

        {/* Botao enviar */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !veiculoId || !todosRespondidos}
          style={{
            width: '100%', padding: '16px', fontSize: '1.1rem', fontWeight: '600',
            background: (isSubmitting || !veiculoId || !todosRespondidos) ? '#ccc' : '#04C64F',
            color: 'white', border: 'none', borderRadius: '12px',
            cursor: (isSubmitting || !veiculoId || !todosRespondidos) ? 'not-allowed' : 'pointer',
            marginBottom: '20px'
          }}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar Checklist'}
        </button>
      </div>
    </div>
  );
};

export default MotoristaChecklist;
